/* Helper functions to transform data into the store format and back */

import {
  FolderOutput as APIFolder,
  LayerOutput as APILayer,
  FileDescriptorRead,
  LayersInner1,
  PublicationReadDetail,
  PublicationVersionCreate,
  PublicationVersionCreateToJSONTyped,
  PublicationVersionFile,
  PublicationVersionFileReference,
  PublicationVersionRead,
} from "@nens/rana/api-v1";
import { randomUUID, removeNullUndefined } from "@nens/shared-utils";

import { CompleteFile } from "../types/file-reference-with-file";
import { Folder, Layer, Publication, PublicationMap } from "../types/publication";
import { getDataTypeFromFile } from "../utils/get-data-type-from-file";

import { getPropertiesFromApiLayer } from "./layer-property-converters";

export function combinePublicationAndVersion(
  isPublic: boolean,
  publicationRead: PublicationReadDetail,
  publicationVersion: PublicationVersionRead,
  files: PublicationVersionFile[],
  descriptors: FileDescriptorRead[],
  latestVersion: number | null
): Publication {
  // Combine file references, files and file descriptors into CompleteFiles.
  const completeFiles: CompleteFile[] = removeNullUndefined(
    publicationVersion.files?.map((fileReference: PublicationVersionFileReference) => {
      // Find the corresponding file object
      const file: PublicationVersionFile | undefined = files.find(
        (f) => f.file.id === fileReference.path
      );

      if (!file) {
        console.log("Did not find file object for fileReference.ref", fileReference.ref);
        return null;
      }

      // Find the corresponding descriptor
      const descriptor: FileDescriptorRead | undefined = descriptors.find(
        (descriptor) => descriptor.id === file.file.descriptorId
      );

      if (!descriptor) {
        console.log("Did not find descriptor for descriptorId", file.file.descriptorId);
        return null;
      }

      return {
        ...fileReference,
        file: {
          ...file.file,
          descriptor,
        },
      };
    }) ?? []
  );

  // If there are no maps, assume a default map
  // Add randomUUIDs for frontend use
  // Make sure the layers prop exists, possibly empty
  // Convert types
  const maps: PublicationMap[] =
    publicationVersion.maps && publicationVersion.maps.length
      ? publicationVersion.maps.map((map): PublicationMap => {
          // Both the map and all its layers need a 'id' property that's not stored
          // on the backend. Add it here.
          return {
            ...map,
            studyArea: map.studyArea ? layerFromAPILayer(map.studyArea, completeFiles) : null,
            layers: addFrontendFieldsToBackendLayers(map.layers ?? [], completeFiles),
            id: randomUUID(),
          };
        })
      : [
          // If the publication has no maps yet, create an initial map. Publications
          // always have at least one map.
          {
            id: randomUUID(),
            name: "Map 1",
            studyArea: null,
            layers: [],
            mapId: null,
          },
        ];

  // The whole publication as it will be saved in Zustand.
  const publication: Publication = {
    id: publicationRead.id,
    creator: publicationRead.creator,
    projectId: publicationRead.project.id,
    projectSlug: publicationRead.project.slug,
    name: publicationRead.name,
    description: publicationRead.description ?? "",
    // The publication's own updatedAt only changes when the publication record changes
    // (name, description, published version), not when a draft version is saved. The save
    // time of the version itself is what "last saved" means here.
    lastSave: publicationVersion.createdAt,
    publishedVersion: publicationRead.publishedVersion ?? null,
    versionId: publicationVersion.id,
    version: publicationVersion.version,
    openMap: maps[0].id /* Always first map initially open */,
    maps,
    files: completeFiles,
    isPublic,
    latestVersion,
    isEditing: publicationRead.publishedVersion !== publicationVersion.version,
  };

  return publication;
}

export function getNewVersionCreateFromPublication(publication: Publication) {
  // Create a PublicationCreate object, and then convert it to JSON so we can send it directly
  const newVersion: PublicationVersionCreate = {
    version: publication.version + 1,
    // The frontend version of files may have extra fields, send the backend
    // only those that should be saved.
    files: publication.files.map(({ name, ref, path }: CompleteFile) => ({
      name,
      ref,
      path,
    })),
    // Maps also have ids that don't need to be saved.
    maps: publication.maps.map(({ mapId, name, layers, studyArea }) => ({
      mapId,
      name,
      layers,
      studyArea,
    })),
  };

  const newVersionJson = PublicationVersionCreateToJSONTyped(newVersion);

  return newVersionJson;
}

function _getFileForLayer(layer: APILayer, files: CompleteFile[]): CompleteFile {
  const file = files.find((file) => layer.filePath === file.path);

  if (!file) {
    throw new Error(`File with path ${layer.filePath} not found in files.`);
  }

  return file;
}

function layerFromAPILayer(apiLayer: APILayer, files: CompleteFile[]): Layer {
  const { styleId, minMax, ...layer } = apiLayer;
  const id = randomUUID();
  const file = _getFileForLayer(apiLayer, files);
  const properties = getPropertiesFromApiLayer(layer.properties, file);

  const frontendLayer: Layer = {
    ...layer,
    type: "layer",
    selected: false,
    layerInFile: layer.layerInFile ?? null,
    visible: layer.visible ?? true,
    opacity: layer.opacity ?? 1,
    properties,
    id,
    dataType: getDataTypeFromFile(file.file),
  };

  return frontendLayer;
}

const folderFromAPIFolder = (folder: APIFolder, files: CompleteFile[]): Folder => {
  const id = randomUUID();
  return {
    ...folder,
    type: "folder",
    layers: addFrontendFieldsToBackendLayers(folder.layers ?? [], files),
    id,
    opened: false,
  };
};

/* Add stuff like id, selected and opened to the layers and folders */
function addFrontendFieldsToBackendLayers(
  layers: LayersInner1[],
  files: CompleteFile[]
): (Layer | Folder)[] {
  return layers.map((layer) => {
    if (layer.type === "layer") {
      return layerFromAPILayer(layer, files);
    } else {
      return folderFromAPIFolder(layer, files);
    }
  });
}
